import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

interface ToastEntry {
  id: number;
  type: ToastType;
  message: string;
}

interface ConfirmState {
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  resolve: (value: boolean) => void;
}

interface ToastContextValue {
  showToast: (message: string, type?: ToastType) => void;
  confirm: (message: string, options?: { confirmLabel?: string; cancelLabel?: string }) => Promise<boolean>;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

// Replaces window.alert(...) — call from anywhere under <ToastProvider>.
export const useToast = (): ToastContextValue => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return ctx;
};

const TOAST_DURATION_MS = 5000;

// Light card matching the site's own palette — white surface, #e6e6e6 border
// (same as .bor10 cards), #333 body text (cl2) — with only the icon + left
// accent bar coloured per type, and info/default reusing the site's actual
// theme colour (#717fe0, the same accent used on button hovers).
const TOAST_STYLES: Record<ToastType, { icon: string; accent: string }> = {
  success: { icon: 'zmdi-check-circle', accent: '#3dbd6f' },
  error: { icon: 'zmdi-close-circle', accent: '#f74877' },
  warning: { icon: 'zmdi-alert-circle-o', accent: '#f39c12' },
  info: { icon: 'zmdi-info-outline', accent: '#717fe0' },
};

const ToastItem: React.FC<{ toast: ToastEntry; onClose: () => void }> = ({ toast, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, TOAST_DURATION_MS);
    return () => clearTimeout(timer);
  }, [onClose]);

  const style = TOAST_STYLES[toast.type];

  return (
    <div
      className="app-toast"
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        minWidth: 280,
        maxWidth: 400,
        padding: '15px 18px',
        borderRadius: 10,
        backgroundColor: '#fff',
        border: '1px solid #e6e6e6',
        borderLeft: `3px solid ${style.accent}`,
        boxShadow: '0 6px 24px rgba(0,0,0,0.1)',
        animation: 'appToastIn 0.3s ease',
      }}
    >
      <i
        className={`zmdi ${style.icon}`}
        style={{ fontSize: 20, color: style.accent, flexShrink: 0, lineHeight: '1.4' }}
      />
      <span
        style={{
          flex: 1, fontSize: 13, lineHeight: 1.6,
          color: '#333', fontFamily: 'Poppins-Regular, sans-serif',
        }}
      >
        {toast.message}
      </span>
      <button
        onClick={onClose}
        style={{
          background: 'none', border: 'none', color: '#999',
          cursor: 'pointer', fontSize: 14, lineHeight: '1.4', padding: 0, flexShrink: 0,
        }}
        aria-label="Close notification"
      >
        <i className="zmdi zmdi-close" />
      </button>
    </div>
  );
};

const ConfirmDialog: React.FC<{
  state: ConfirmState;
  onResult: (value: boolean) => void;
}> = ({ state, onResult }) => (
  <div
    style={{
      position: 'fixed', inset: 0, zIndex: 10000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.55)', padding: 20,
    }}
    onClick={() => onResult(false)}
  >
    <div
      className="app-confirm-dialog"
      style={{
        background: '#fff', borderRadius: 12, padding: '32px 34px',
        maxWidth: 420, width: '100%', boxShadow: '0 15px 50px rgba(0,0,0,0.3)',
        textAlign: 'center',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <i
        className="zmdi zmdi-alert-circle-o"
        style={{ fontSize: 34, color: '#f39c12', display: 'block', marginBottom: 14 }}
      />
      <p
        style={{
          margin: '0 0 28px', fontSize: 14, lineHeight: 1.7, color: '#333',
          fontFamily: 'Poppins-Regular, sans-serif',
        }}
      >
        {state.message}
      </p>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 14 }}>
        <button
          className="app-confirm-btn app-confirm-btn--cancel"
          onClick={() => onResult(false)}
        >
          {state.cancelLabel || 'Cancel'}
        </button>
        <button
          className="app-confirm-btn app-confirm-btn--ok"
          onClick={() => onResult(true)}
        >
          {state.confirmLabel || 'Confirm'}
        </button>
      </div>
    </div>
  </div>
);

let toastIdCounter = 0;

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const [confirmState, setConfirmState] = useState<ConfirmState | null>(null);
  const confirmStateRef = useRef<ConfirmState | null>(null);
  confirmStateRef.current = confirmState;

  const showToast = useCallback((message: string, type: ToastType = 'info') => {
    const id = ++toastIdCounter;
    setToasts((prev) => [...prev, { id, type, message }]);
  }, []);

  const dismissToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // Replaces window.confirm(...) — await the returned promise for the user's choice.
  const confirm = useCallback((
    message: string,
    options?: { confirmLabel?: string; cancelLabel?: string }
  ): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      setConfirmState({ message, resolve, ...options });
    });
  }, []);

  const handleConfirmResult = (value: boolean) => {
    confirmStateRef.current?.resolve(value);
    setConfirmState(null);
  };

  return (
    <ToastContext.Provider value={{ showToast, confirm }}>
      {children}

      <div className="app-toast-stack">
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onClose={() => dismissToast(toast.id)} />
        ))}
      </div>

      {confirmState && <ConfirmDialog state={confirmState} onResult={handleConfirmResult} />}

      <style>{`
        .app-toast-stack {
          position: fixed;
          bottom: 24px;
          right: 30px;
          z-index: 9999;
          display: flex;
          flex-direction: column-reverse;
          gap: 12px;
        }
        @media (max-width: 575px) {
          .app-toast-stack {
            right: 15px;
            left: 15px;
          }
          .app-toast {
            max-width: none !important;
          }
        }
        @keyframes appToastIn {
          from { transform: translateX(30px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        .app-confirm-btn {
          font-family: 'Poppins-Medium', sans-serif;
          font-size: 13px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          padding: 11px 26px;
          border-radius: 24px;
          cursor: pointer;
          transition: all 0.3s;
        }
        .app-confirm-btn--cancel {
          background: #fff;
          color: #333;
          border: 1px solid #e6e6e6;
        }
        .app-confirm-btn--cancel:hover {
          border-color: #717fe0;
          background-color: #717fe0;
          color: #fff;
        }
        .app-confirm-btn--ok {
          background: #717fe0;
          color: #fff;
          border: 1px solid #717fe0;
        }
        .app-confirm-btn--ok:hover {
          border-color: #5c68c9;
          background-color: #5c68c9;
        }
      `}</style>
    </ToastContext.Provider>
  );
};
